import React, { useEffect, useState, useMemo } from "react";
import {
  CheckCircleFilled,
  CreditCardOutlined,
  ExclamationCircleOutlined,
  HistoryOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  message,
  Progress,
  Row,
  Skeleton,
  Slider,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { useAuthenticatedFetch } from "@/Api/Axios";
import { detectUserCountry } from "@/Utils/countryDetection";

const { Title, Text } = Typography;

/**
 * BillingTab Component
 * Shows credit balance, slider-based purchase options, and transaction history
 */
const BillingTab = () => {
  const fetch = useAuthenticatedFetch();
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [creditBalance, setCreditBalance] = useState(null);
  const [packages, setPackages] = useState([]);
  const [pricingInfo, setPricingInfo] = useState(null);
  const [selectedPackageIndex, setSelectedPackageIndex] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [transactionsPagination, setTransactionsPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
  });
  const [userCountry] = useState(() => detectUserCountry());

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const shouldRefresh = urlParams.get("refresh");
    const sessionId = urlParams.get("session_id");
    const paymentSuccess = urlParams.get("payment_success");
    const paymentCancelled = urlParams.get("payment_cancelled");

    if (shouldRefresh === "true") {
      loadCreditData();
      message.success("Credit balance updated!");
      window.history.replaceState({}, document.title, window.location.pathname + "?tab=billing");
    } else if (sessionId && paymentSuccess === "true") {
      handlePaymentSuccess(sessionId);
    } else if (paymentCancelled === "true") {
      loadCreditData();
      message.info("Payment was cancelled");
      window.history.replaceState({}, document.title, window.location.pathname);
    } else {
      loadCreditData();
    }
  }, []);

  // Set default slider to the popular package when packages load
  useEffect(() => {
    if (packages.length > 0) {
      const popularIdx = packages.findIndex((p) => p.popular);
      if (popularIdx >= 0) {
        setSelectedPackageIndex(popularIdx);
      }
    }
  }, [packages]);

  const loadCreditData = async () => {
    try {
      setLoading(true);
      let hasError = false;

      try {
        const balanceResponse = await fetch.get("/credits/balance", false);
        if (balanceResponse?.success) {
          setCreditBalance(balanceResponse.data);
        }
      } catch (err) {
        console.error("Error loading credit balance:", err);
        hasError = true;
      }

      try {
        const packagesResponse = await fetch.get(`/credits/packages?country=${userCountry}`, false);
        if (packagesResponse?.success) {
          setPackages(packagesResponse.data.packages || []);
          setPricingInfo(packagesResponse.data.pricing || null);
        }
      } catch (err) {
        console.error("Error loading credit packages:", err);
        hasError = true;
      }

      try {
        await loadTransactions(1);
      } catch (err) {
        console.error("Error loading transaction history:", err);
        hasError = true;
      }

      if (hasError) {
        message.error("Some billing data could not be loaded. Please refresh the page or contact support if the issue persists.");
      }
    } catch (error) {
      console.error("Error loading credit data:", error);
      message.error("Failed to load billing data");
    } finally {
      setLoading(false);
    }
  };

  const loadTransactions = async (page) => {
    try {
      const response = await fetch.get(`/credits/history?page=${page}&limit=10`, false);
      if (response?.success) {
        setTransactions(response.data.transactions || []);
        setTransactionsPagination({
          page: response.data.pagination.page,
          limit: response.data.pagination.limit,
          total: response.data.pagination.total,
        });
      }
    } catch (error) {
      console.error("Error loading transactions:", error);
      setTransactions([]);
    }
  };

  const handlePurchase = async (packageId) => {
    try {
      setPurchasing(true);
      const response = await fetch.post("/credits/purchase/initiate", { packageId, country: userCountry }, false);

      if (!response?.success) {
        throw new Error(response?.message || "Failed to initiate checkout");
      }

      const { checkoutUrl } = response.data;
      const paymentTab = window.open(checkoutUrl, "_blank");

      if (!paymentTab) {
        message.error("Popup blocked! Please allow popups for this site and try again.");
      } else {
        message.info("Payment page opened in new tab. Complete payment there.");
      }
    } catch (error) {
      console.error("Purchase error:", error);
      message.error(error.message || "Failed to open payment page");
    } finally {
      setPurchasing(false);
    }
  };

  const handlePaymentSuccess = async (sessionId) => {
    try {
      const verifyResponse = await fetch.post("/credits/purchase/verify", { sessionId });
      if (verifyResponse?.success) {
        message.success(verifyResponse.data.message || "Credits added successfully!");
        loadCreditData();
      } else {
        message.warning("Payment received but verification pending. Credits will be added shortly.");
      }
    } catch (error) {
      console.error("Payment verification error:", error);
      message.warning("Payment received but verification pending. Please contact support if credits are not added.");
    } finally {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  // Build slider marks from packages
  const sliderMarks = useMemo(() => {
    const marks = {};
    packages.forEach((pkg, idx) => {
      const credits = pkg.credits;
      let label = credits >= 1000 ? `${credits / 1000}k` : `${credits}`;
      marks[idx] = {
        label: <span style={{ fontSize: "12px", color: idx === selectedPackageIndex ? "#1890ff" : "#9ca3af", fontWeight: idx === selectedPackageIndex ? 600 : 400 }}>{label}</span>,
      };
    });
    return marks;
  }, [packages, selectedPackageIndex]);

  const selectedPackage = packages[selectedPackageIndex] || null;

  // Transaction table columns
  const transactionColumns = [
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      render: (date) =>
        new Date(date).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 130,
      render: (type) => {
        const typeConfig = {
          deduction: { color: "red", icon: <MinusCircleOutlined />, label: "Usage" },
          purchase: { color: "green", icon: <PlusCircleOutlined />, label: "Purchase" },
          signup_bonus: { color: "blue", icon: <ThunderboltOutlined />, label: "Bonus" },
          bonus: { color: "purple", icon: <ThunderboltOutlined />, label: "Bonus" },
          refund: { color: "orange", icon: <ReloadOutlined />, label: "Refund" },
        };
        const config = typeConfig[type] || { color: "default", icon: null, label: type };
        return (
          <Tag color={config.color} icon={config.icon}>
            {config.label}
          </Tag>
        );
      },
    },
    {
      title: "Feature",
      dataIndex: "featureName",
      key: "featureName",
      render: (name, record) => name || record.description || "-",
    },
    {
      title: "Credits",
      dataIndex: "amount",
      key: "amount",
      width: 100,
      align: "right",
      render: (amount) => (
        <Text style={{ color: amount > 0 ? "#22c55e" : "#ef4444", fontWeight: 600 }}>
          {amount > 0 ? `+${amount}` : amount}
        </Text>
      ),
    },
    {
      title: "Balance",
      dataIndex: "balanceAfter",
      key: "balanceAfter",
      width: 100,
      align: "right",
      render: (balance) => <Text strong>{balance}</Text>,
    },
  ];

  if (loading) {
    return (
      <div style={{ padding: "24px" }}>
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }

  const balance = creditBalance?.balance || 0;
  const totalUsed = creditBalance?.totalUsed || 0;
  const freeCredits = creditBalance?.freeCreditsGiven || 30;

  return (
    <div style={{ maxWidth: "1000px" }}>
      {/* Credit Balance Card */}
      <Card
        style={{
          borderRadius: "12px",
          marginBottom: "20px",
          background: "linear-gradient(135deg, #1990ff 0%, #0066cc 100%)",
          border: "none",
        }}
        bodyStyle={{ padding: "16px 20px" }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <Title level={3} style={{ color: "#fff", margin: 0, fontSize: "28px", fontWeight: 700 }}>
                {balance}
              </Title>
              <ThunderboltOutlined style={{ fontSize: "18px", color: "rgba(255,255,255,0.9)" }} />
            </div>
            <div>
              <Text style={{ color: "rgba(255,255,255,0.9)", fontSize: "13px", display: "block", fontWeight: 500 }}>
                Available Credits
              </Text>
              <Text style={{ color: "rgba(255,255,255,0.7)", fontSize: "11px" }}>
                {totalUsed} used{creditBalance?.totalPurchased > 0 && ` • ${creditBalance.totalPurchased} purchased`}
              </Text>
            </div>
          </div>
          <div style={{ flex: "1", minWidth: "200px", maxWidth: "400px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
              <Text style={{ color: "rgba(255,255,255,0.9)", fontSize: "12px", fontWeight: 500 }}>
                Usage
              </Text>
              <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: "11px", fontWeight: 600 }}>
                {totalUsed}/{freeCredits + (creditBalance?.totalPurchased || 0)}
              </Text>
            </div>
            <Progress
              percent={Math.min(100, (totalUsed / (freeCredits + (creditBalance?.totalPurchased || 0))) * 100)}
              strokeColor="#fff"
              trailColor="rgba(255,255,255,0.25)"
              showInfo={false}
              strokeWidth={5}
            />
          </div>
        </div>
      </Card>

      {/* Low Credit Warning */}
      {balance < 5 && (
        <Alert
          message={
            <span style={{ fontSize: "13px" }}>
              <strong>Low Balance:</strong> Less than 5 credits remaining. Purchase more to continue.
            </span>
          }
          type="warning"
          showIcon
          closable
          style={{ marginBottom: "20px", borderRadius: "8px", padding: "8px 12px" }}
          icon={<ExclamationCircleOutlined style={{ fontSize: "16px" }} />}
        />
      )}

      {/* Slider + Plan Selection */}
      <style>{`
        .billing-pricing-container {
          max-width: 560px;
          margin: 0 auto 28px;
          background: #fff;
          border-radius: 16px;
          border: 1px solid #e5e7eb;
          box-shadow: 0 2px 12px rgba(0,0,0,0.05);
          padding: 28px 28px 24px;
        }
        .billing-pricing-header {
          text-align: center;
          margin-bottom: 24px;
        }
        .billing-pricing-header h3 {
          font-size: 20px;
          font-weight: 700;
          color: #1f2937;
          margin: 0 0 6px;
          letter-spacing: -0.3px;
        }
        .billing-pricing-header p {
          font-size: 13px;
          color: #9ca3af;
          margin: 0;
          font-weight: 400;
        }
        .billing-pricing-header p strong {
          color: #6b7280;
          font-weight: 600;
        }
        .billing-slider-wrap {
          padding: 0 4px 12px;
        }
        .billing-slider-wrap .ant-slider {
          margin: 0 0 4px;
          height: 32px;
          padding-block: 10px;
        }
        .billing-slider-wrap .ant-slider-rail {
          background: #ebebeb;
          height: 4px !important;
          border-radius: 2px !important;
          top: 50%;
          transform: translateY(-50%);
        }
        .billing-slider-wrap .ant-slider-track {
          background: #1890ff !important;
          height: 14px !important;
          border-radius: 8px !important;
          top: 50%;
          transform: translateY(-50%);
          min-width: 18px !important;
        }
        .billing-slider-wrap .ant-slider-handle {
          width: 20px !important;
          height: 20px !important;
          border: none !important;
          border-radius: 50% !important;
          background: transparent !important;
          box-shadow: none !important;
          inset-block-start: -2px !important;
          opacity: 0 !important;
          cursor: pointer;
        }
        .billing-slider-wrap .ant-slider-handle::before {
          display: none !important;
        }
        .billing-slider-wrap .ant-slider-handle::after {
          display: none !important;
        }
        .billing-slider-wrap .ant-slider-handle:hover,
        .billing-slider-wrap .ant-slider-handle:focus {
          box-shadow: none !important;
          background: #ffffff !important;
        }
        .billing-slider-wrap .ant-slider-dot {
          display: none !important;
        }
        .billing-slider-wrap .ant-slider-mark {
          top: 26px;
        }
        .billing-slider-wrap .ant-slider-mark-text {
          font-size: 11px;
          color: #b0b0b0;
          font-weight: 500;
          user-select: none;
        }
        .billing-slider-wrap .ant-slider-mark-text-active {
          color: #1890ff;
          font-weight: 700;
        }
        .billing-plan-divider {
          height: 1px;
          background: #f0f0f0;
          margin: 16px 0 20px;
        }
        .billing-plan-card {
          padding: 0;
        }
        .billing-plan-badge {
          display: inline-block;
          background: #e6f7ff;
          color: #1890ff;
          font-size: 11px;
          font-weight: 600;
          padding: 3px 10px;
          border-radius: 10px;
          margin-bottom: 10px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .billing-plan-sublabel {
          font-size: 13px;
          font-weight: 500;
          color: #9ca3af;
          margin-bottom: 4px;
        }
        .billing-plan-price-row {
          display: flex;
          align-items: baseline;
          gap: 6px;
          margin-bottom: 20px;
        }
        .billing-plan-price-amount {
          font-size: 42px;
          font-weight: 800;
          color: #1890ff;
          line-height: 1;
          letter-spacing: -1px;
        }
        .billing-plan-price-suffix {
          font-size: 14px;
          color: #b0b0b0;
          font-weight: 500;
        }
        .billing-plan-features {
          list-style: none;
          padding: 0;
          margin: 0 0 24px 0;
        }
        .billing-plan-features li {
          display: flex;
          align-items: center;
          gap: 9px;
          padding: 6px 0;
          font-size: 14px;
          color: #374151;
        }
        .billing-plan-features li .bp-check {
          color: #1890ff;
          font-size: 14px;
          flex-shrink: 0;
        }
        .billing-plan-btn {
          width: 100%;
          height: 48px !important;
          border-radius: 10px !important;
          font-weight: 700 !important;
          font-size: 15px !important;
          background: #1890ff !important;
          color: #fff !important;
          border: none !important;
          transition: background 0.15s !important;
        }
        .billing-plan-btn:hover {
          background: #40a9ff !important;
        }
        .billing-plan-btn:active {
          background: #096dd9 !important;
        }
        .billing-stripe-note {
          text-align: center;
          margin-top: 16px;
        }
        @media (max-width: 640px) {
          .billing-pricing-container {
            max-width: 100%;
            padding: 20px 16px;
          }
          .billing-plan-price-amount {
            font-size: 34px;
          }
        }
      `}</style>

      {packages.length > 0 && (
        <div className="billing-pricing-container">
          <div className="billing-pricing-header">
            <h3>Choose your credit pack</h3>
            <p>Bigger packs, <strong>better price per credit</strong></p>
          </div>

          <div className="billing-slider-wrap">
            <Slider
              min={0}
              max={packages.length - 1}
              step={1}
              marks={sliderMarks}
              value={selectedPackageIndex}
              onChange={(val) => setSelectedPackageIndex(val)}
              tooltip={{ open: false }}
            />
          </div>

          {selectedPackage && (
            <div className="billing-plan-card">
              <div className="billing-plan-divider" />
              <span className="billing-plan-badge">{selectedPackage.name}</span>
              <div className="billing-plan-sublabel">Suggested plan</div>
              <div className="billing-plan-price-row">
                <span className="billing-plan-price-amount">
                  {selectedPackage.priceDisplay}
                </span>
                <span className="billing-plan-price-suffix">/one-time</span>
              </div>

              <ul className="billing-plan-features">
                <li>
                  <CheckCircleFilled className="bp-check" />
                  <span>{selectedPackage.credits.toLocaleString()} AI credits</span>
                </li>
                <li>
                  <CheckCircleFilled className="bp-check" />
                  <span>{selectedPackage.pricePerCreditDisplay || selectedPackage.pricePerCredit} per credit</span>
                </li>
                {selectedPackage.savingsDisplay && (
                  <li>
                    <CheckCircleFilled className="bp-check" />
                    <span>{selectedPackage.savingsDisplay}</span>
                  </li>
                )}
                <li>
                  <CheckCircleFilled className="bp-check" />
                  <span>All AI features included</span>
                </li>
                <li>
                  <CheckCircleFilled className="bp-check" />
                  <span>No expiry on credits</span>
                </li>
                <li>
                  <CheckCircleFilled className="bp-check" />
                  <span>Resume parsing, scoring & enhancement</span>
                </li>
              </ul>

              <Button
                className="billing-plan-btn"
                onClick={() => handlePurchase(selectedPackage.id)}
                loading={purchasing}
              >
                Buy {selectedPackage.credits.toLocaleString()} Credits
              </Button>
            </div>
          )}

          <div className="billing-stripe-note">
            <Text style={{ color: "#b0b0b0", fontSize: "12px" }}>
              <CreditCardOutlined style={{ marginRight: "5px" }} />
              Secure payments via Stripe
            </Text>
          </div>
        </div>
      )}

      {/* Transaction History */}
      <Card
        title={
          <Space>
            <HistoryOutlined />
            <span>Transaction History</span>
          </Space>
        }
        style={{ borderRadius: "16px" }}
      >
        {transactions.length > 0 ? (
          <Table
            dataSource={transactions}
            columns={transactionColumns}
            rowKey="_id"
            pagination={{
              current: transactionsPagination.page,
              pageSize: transactionsPagination.limit,
              total: transactionsPagination.total,
              showSizeChanger: false,
              onChange: (page) => loadTransactions(page),
            }}
            size="small"
            scroll={{ x: 500 }}
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No transactions yet"
            style={{ padding: "40px 0" }}
          />
        )}
      </Card>

      {/* Feature Costs Reference */}
      {creditBalance?.featureCosts && (
        <Card
          title={
            <Space>
              <ThunderboltOutlined />
              <span>Feature Credit Costs</span>
            </Space>
          }
          style={{ borderRadius: "16px", marginTop: "24px" }}
        >
          <Row gutter={[16, 12]}>
            {Object.entries(creditBalance.featureCosts).map(([key, cost]) => (
              <Col xs={12} md={8} key={key}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "8px 12px",
                    background: "#f8fafc",
                    borderRadius: "8px",
                  }}
                >
                  <Text style={{ color: "#4b5563" }}>
                    {creditBalance.featureNames?.[key] || key}
                  </Text>
                  <Text strong style={{ color: "#1990ff" }}>
                    {cost} credits
                  </Text>
                </div>
              </Col>
            ))}
          </Row>
        </Card>
      )}
    </div>
  );
};

export default BillingTab;
